import { CdkDragDrop, CdkDragMove, moveItemInArray } from '@angular/cdk/drag-drop';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  Signal,
  signal,
  viewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, filter, pairwise } from 'rxjs/operators';
import { AuthService } from 'src/app/auth.service';
import { LoadingState } from 'src/app/shared/components/ui/content-display-card/content-display-card.component';
import { generateObjectId } from 'src/app/shared/utils/object-id.utils';
import { Chunk } from './chunk/chunk.dto';
import { ChunkService } from './chunk/chunk.service';
import { CreateChunkComponent } from './chunk/create-chunk/create-chunk.component';
import { CreateChunkService } from './chunk/create-chunk/create-chunk.service';
import { ContentStateService } from './content-state.service';
import { ContentService } from './content.service';

@Component({
  selector: 'app-content',
  templateUrl: './content.component.html',
  styleUrls: ['./content.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly renderer = inject(Renderer2);

  private readonly contentService = inject(ContentService);
  readonly stateService = inject(ContentStateService);
  private readonly translate = inject(TranslateService);
  private readonly chunkService = inject(ChunkService);
  private readonly createChunkService = inject(CreateChunkService);
  private readonly authService = inject(AuthService);

  private scrollInterval: any;
  private lastPlaceholderUpdate = 0;
  private placeholderUpdateThrottle = 50; // ms

  course = input<any>();
  blogPost = input<any>();
  courseID = input<string>();
  blogID = input<string>();
  note = input<any>();
  noteID = input<string>();
  lecture = input<any>();
  section = input<any>();
  contentID = input<string>();
  contentName = input<string>();
  contentType = input<string>();
  dialogData = input<any>();
  from = input<'pop-content' | 'blog' | string>();
  shareToken = input<string>();

  player = viewChild.required<ElementRef<HTMLAudioElement>>('player');
  @ViewChildren(CreateChunkComponent) chunkCreators: QueryList<CreateChunkComponent>;

  private readonly _loading: Signal<LoadingState> = this.stateService.loading;
  loading: Signal<boolean> = computed(() => this._loading().status === 'loading' && this._loading().reason === 'fetch-data');
  lectureFinished = this.stateService.lectureFinished;
  content = this.stateService.content;
  hasChunkWithCreationErrors = this.stateService.hasChunkWithCreationErrors;

  readMode = input(true);
  dragMode = input(false);
  createMode = input(false);
  hardReloadOnLoad = input(false);
  activeInsertPlaceholder = toSignal(this.contentService.insertBelowChunk$);
  insertChunkType = signal<string | null>(null);

  editingChunk = signal('');
  videoWidth = signal(window.innerWidth < 870 ? window.innerWidth - 100 : 870);
  contentLanguage = signal(this.translate.currentLang || 'fr');

  isDraggingOver = signal(false);
  isChunkDragging = signal(false);
  insertBelowId = signal<string | null>(null);
  uploadState = this.stateService.uploadState;
  private lastPlayedChunkId: string | undefined;
  private currentPlayingSrc: string | null = null;

  isReader = computed(() => {
    if (this.shareToken()) {
      return true;
    }
    const userId = this.authService.getID();
    const course = this.course();
    if (course && course.users) {
      const user = course.users.find((u: any) => (u._id || u.id) === userId);
      return user?.role === 'Reader';
    }
    const blog = this.blogPost();
    if (blog && blog.users) {
      const user = blog.users.find((u: any) => (u._id || u.id) === userId);
      return user?.role === 'Reader';
    }
    const note = this.note();
    if (note) {
      return note.creator !== userId;
    }
    return false;
  });

  isContentEditor = computed(() => {
    if (this.shareToken()) {
      return false;
    }
    const userId = this.authService.getID();
    const course = this.course();
    if (course) {
      if (course.creator === userId) return true;
      if (course.users) {
        const user = course.users.find((u: any) => (u._id || u.id) === userId);
        if (user && (user.role === 'Writer' || user.role === 'Admin')) return true;
      }
    }
    const blog = this.blogPost();
    if (blog) {
      if (blog.creator === userId) return true;
      if (blog.users) {
        const user = blog.users.find((u: any) => (u._id || u.id) === userId);
        if (user && (user.role === 'Writer' || user.role === 'Admin')) return true;
      }
    }
    const note = this.note();
    if (note) {
      return note.creator === userId;
    }
    return false;
  });

  constructor() {
    effect(() => {
      this.stateService.setReaderState(this.isReader());
      this.stateService.setContentEditorState(this.isContentEditor());
    });

    effect(() => {
      const placeholder = this.activeInsertPlaceholder();
      if (!placeholder) {
        this.insertChunkType.set(null);
      }
    });

    effect(() => {
      const chunks = this.content();
      const isLoading = this.loading();
      if (!isLoading && chunks.length === 1 && !this.isReader()) {
        const firstChunk = chunks[0];
        const contentVal = firstChunk.content;
        const isEmpty = !contentVal || contentVal.trim() === '' || contentVal === '<p></p>' || contentVal === '<p><br></p>' || contentVal === '<p><br/></p>';
        if (isEmpty && this.stateService.highlightedChunkId() !== firstChunk._id) {
          setTimeout(() => {
            this.stateService.setHighlightedChunkId(firstChunk._id);
          });
        }
      }
    });

    // Player state management
    toObservable(this.stateService.playerState)
      .pipe(debounceTime(10), takeUntilDestroyed(this.destroyRef))
      .subscribe((playerState) => {
        const playerEl = this.player()?.nativeElement;
        if (!playerEl) return;

        if (playerState.chunkId && playerState.status === 'playing') {
          this.scrollIntoView(playerState.chunkId);
        }
        const newSrc = playerState.sourceUrl ? playerState.sourceUrl.toString() : '';
        const chunkChanged = this.lastPlayedChunkId !== playerState.chunkId;
        this.lastPlayedChunkId = playerState.chunkId;

        if (newSrc && (this.currentPlayingSrc !== newSrc || chunkChanged)) {
          this.currentPlayingSrc = newSrc;
          playerEl.src = newSrc;
          playerEl.load();
        }

        playerEl.playbackRate = playerState.playbackRate || 1;

        if (playerState.status === 'playing' && playerState.sourceUrl) {
          playerEl.play().catch((e) => console.error('Audio play failed:', e));
        } else if (playerState.status === 'paused' || !playerState.sourceUrl) {
          playerEl.pause();
          if (!playerState.sourceUrl) {
            this.currentPlayingSrc = null;
          }
        }
      });

    // Player commands
    this.stateService.mainPlayerCommand$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((command) => {
      if (command === 'clear') {
        const player = this.player()?.nativeElement;
        if (player) {
          player.pause();
          player.src = '';
          this.currentPlayingSrc = null;
          player.load();
        }
      }
    });

    // Language changes
    this.translate.onLangChange.subscribe((event) => {
      this.contentLanguage.set(event.lang);
      this.stateService.getPieces(true, true);
    });

    // Chunk focus
    this.chunkService.chunkToFocusOn$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((chunkId) => {
      if (!this.stateService.isLiveTranscribing() && chunkId) {
        this.stateService.setHighlightedChunkId(chunkId);
      }
    });

    // Re-initialize content when contentID changes
    effect(() => {
      const contentId = this.contentID();
      if (contentId) {
        this.stateService.initialize(
          {
            contentId: contentId,
            courseId: this.courseID(),
            blogId: this.blogID(),
            section: this.section(),
            lecture: this.lecture(),
            noteId: this.noteID(),
            contentName: this.contentName(),
            contentType: this.contentType(),
            shareToken: this.shareToken(),
          },
          this.hardReloadOnLoad()
        );
      }
    });

    // Select last chunk on new content load or create first chunk
    toObservable(this.loading)
      .pipe(
        pairwise(),
        filter(([prev, curr]) => prev === true && curr === false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const chunks = this.content();
        if (chunks.length > 0) {
          // const lastChunk = chunks[chunks.length - 1];
          // this.stateService.setHighlightedChunkId(lastChunk._id);
        } else if (this.contentID()) {
          this.createEmptyChunk();
        }
      });
  }

  ngAfterViewInit(): void {
    this.chunkCreators.changes
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((list) => list.length > 0)
      )
      .subscribe((list) => {
        // Focus the new editor instance, which is usually the last one
        list.last.focusEditor();
      });
  }

  ngOnInit() {
    // All initialization logic is now in the constructor effects
  }

  ngOnDestroy() {
    this.clearScrollInterval();
  }

  createEmptyChunk(): void {
    const chunkData = {
      content: '',
      type: 'html',
      language: this.contentLanguage(),
      generateVoice: false,
      generateTranslation: false,
      generateEmbeddings: false,
    };

    this.chunkService.createChunk({ chunks: [chunkData] }).subscribe();
  }

  private clearScrollInterval() {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  private updateInsertPlaceholder(y: number) {
    const now = Date.now();
    if (now - this.lastPlaceholderUpdate < this.placeholderUpdateThrottle) {
      return;
    }
    this.lastPlaceholderUpdate = now;

    const chunkElements = this.document.querySelectorAll('.chunk-wrapper:not(.cdk-drag-placeholder)');
    if (chunkElements.length === 0) {
      this.insertBelowId.set('first');
      return;
    }

    let newInsertId: string | null = null;
    const margin = 20;

    const firstChunkRect = chunkElements[0].getBoundingClientRect();
    if (y < firstChunkRect.top + margin) {
      newInsertId = 'first';
    } else {
      for (let i = 0; i < chunkElements.length - 1; i++) {
        const currentChunkRect = chunkElements[i].getBoundingClientRect();
        const nextChunkRect = chunkElements[i + 1].getBoundingClientRect();
        const gapTop = currentChunkRect.bottom - margin;
        const gapBottom = nextChunkRect.top + margin;

        if (y > gapTop && y < gapBottom) {
          newInsertId = chunkElements[i].id;
          break;
        }
      }
    }

    if (newInsertId === null) {
      const lastChunk = chunkElements[chunkElements.length - 1];
      const lastChunkRect = lastChunk.getBoundingClientRect();
      if (y > lastChunkRect.bottom - margin) {
        newInsertId = lastChunk.id;
      }
    }

    this.insertBelowId.set(newInsertId);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver.set(true);
    this.updateInsertPlaceholder(event.clientY);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver.set(false);
    this.insertBelowId.set(null);
    this.clearScrollInterval();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver.set(false);
    this.clearScrollInterval();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      const insertAsFirst = this.insertBelowId() === 'first';
      const insertId = insertAsFirst ? null : this.insertBelowId();
      this.insertBelowId.set(null);

      this.stateService.uploadFile(file, insertId, insertAsFirst);
    }
  }

  onChunkDragStart() {
    this.isChunkDragging.set(true);
  }

  onChunkDragEnd() {
    this.isChunkDragging.set(false);
    this.insertBelowId.set(null);
  }

  onChunkDragMoved(event: CdkDragMove<Chunk>) {
    this.updateInsertPlaceholder(event.pointerPosition.y);
  }

  isChunkProcessing(chunk: Chunk): boolean {
    if (!chunk || !chunk._id) return false;
    if (chunk.isPlaceHolder) return true;
    const tasks = this.stateService.activeTasks();
    const chunkTasks = tasks[chunk._id];
    if (chunkTasks) {
      const hasActive = Object.entries(chunkTasks).some(([taskType, task]: [string, any]) => taskType !== 'embedding' && task?.status === 'generating');
      if (hasActive) return true;
    }
    const playerState = this.stateService.playerState();
    const isPlayingOrBuffering = (playerState.status === 'playing' || playerState.status === 'buffering') && playerState.chunkId === chunk._id;
    if (isPlayingOrBuffering) return true;

    return false;
  }

  drop(event: CdkDragDrop<Chunk[]>): void {
    const updatedContent = [...this.content()];
    moveItemInArray(updatedContent, event.previousIndex, event.currentIndex);
    this.stateService.saveContentOrder(updatedContent);
  }

  onTimeUpdate(): void {
    const playerEl = this.player().nativeElement;
    this.stateService.handleTimeUpdate(playerEl.currentTime);
  }

  onLoadedMetadata(): void {
    const playerEl = this.player().nativeElement;
    this.stateService.handleDurationChange(playerEl.duration);
  }

  onAudioEnded(): void {
    this.stateService.handleAudioEnded();
  }

  trackById(index: number, item: Chunk): string {
    return item.alternateID || item._id;
  }

  scrollIntoView(id?: string): void {
    if (!id) return;
    this.document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }

  toggleEditChunk(id: string): void {
    if (this.stateService.isLiveTranscribing() && id !== this.stateService.highlightedChunkId()) {
      return;
    }
    this.editingChunk.update((current) => (current === id ? '' : id));
  }

  insertLatexAt(id: string): void {
    this.insertChunkType.set('latex');
    this.contentService.setInsertBelowChunk(id);
  }

  insertAt(id: string | null): void {
    this.insertChunkType.set(null);
    this.createDirectChunk(id);
  }

  async createDirectChunk(insertBelowId: string | null): Promise<void> {
    const chunkId = generateObjectId();
    const chunkInfo = {
      content: '',
      tags: [],
      courseID: this.courseID(),
      blogID: this.blogID(),
      lecture: this.lecture(),
      section: this.section(),
      noteID: this.noteID(),
      contentID: this.contentID(),
      contentName: this.contentName(),
      isolateChunk: false,
      type: 'html',
      codeLanguage: undefined,
      contentType: this.contentType(),
      language: this.contentLanguage(),
      generateVoice: true,
      selectedVoice: undefined,
      voiceGender: undefined,
      generateTranslation: true,
      generateEmbeddings: undefined,
      _id: chunkId,
      status: 'creating',
    };

    try {
      const optimisticChunks = await this.chunkService.createChunksInIndex([chunkInfo], insertBelowId || undefined);
      optimisticChunks.forEach((chunk: any) => this.chunkService.passChunkCreatedOnCacheEvent(chunk));

      this.stateService.insertChunksLocally(optimisticChunks, insertBelowId || '');

      // Focus the newly created chunk
      this.chunkService.setChunkToFocusOn(chunkId);

      // Send API call in background
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const apiPayload = optimisticChunks.map((chunk: any) => ({
        ...chunk.chunkParams,
        alternateID: chunk._id,
        _id: chunk._id,
        user: {
          id: userData.id,
          fullName: `${userData.firstName} ${userData.lastName}`,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
        },
      }));

      this.chunkService
        .createChunk({
          chunks: apiPayload,
          insertBelowID: insertBelowId,
        })
        .subscribe();
    } catch (error) {
      console.error('Failed to create chunk directly:', error);
    }
  }
}
